import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, RotateCcw, Watch, Edit3, Lightbulb, Home, MessageSquare } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { useFacialScan } from '../../contexts/FacialScanContext';

export default function ScanErrorPage() {
  const navigate = useNavigate();
  const { errorReason, retryCount, retryScan, resetScan } = useFacialScan();

  const canRetry = retryCount < 2;

  const getTips = () => {
    switch (errorReason) {
      case 'Poor lighting detected':
        return [
          'Move to a brighter area',
          'Face a window or light source',
          'Avoid harsh shadows on your face'
        ];
      case 'Too much movement':
        return [
          'Rest your device on a stable surface',
          'Stay as still as possible',
          'Take a deep breath and relax'
        ];
      case 'Face not clearly visible':
        return [
          'Remove glasses, hats, or face coverings',
          'Center your face in the frame',
          'Ensure your full face is visible'
        ];
      case 'Signal quality too low':
        return [
          'Improve lighting conditions',
          'Hold the device closer to your face',
          'Clean your camera lens'
        ];
      default:
        return [
          'Ensure good lighting',
          'Stay still during the scan',
          'Remove obstructions from your face'
        ];
    }
  };

  const handleRetry = () => {
    retryScan();
    navigate('/scan/start');
  };

  const handleSyncWearable = () => {
    resetScan();
    navigate('/onboarding/device');
  };

  const handleManualEntry = () => {
    resetScan();
    navigate('/manual-entry');
  };

  const handleContactSupport = () => {
    resetScan();
    navigate('/support/contact');
  };

  const handleGoHome = () => {
    resetScan();
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Error Header */}
      <div className="bg-gradient-to-b from-[#D54B48]/10 to-transparent pt-12 pb-8 px-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', duration: 0.6 }}
          className="flex justify-center mb-4"
        >
          <div className="p-4 rounded-full bg-[#D54B48]/20 border-2 border-[#D54B48]">
            <AlertCircle size={40} className="text-[#D54B48]" />
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <h1 className="font-heading font-bold text-2xl mb-2">We Couldn't Verify</h1>
          <p className="text-muted-foreground">{errorReason || 'Something went wrong'}</p>
        </motion.div>
      </div>

      <main className="px-4 pb-8 -mt-2">
        {/* Tips Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="glass-card p-5 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Lightbulb size={18} className="text-[#F8B64C]" />
              <h3 className="font-semibold">Tips for a better scan</h3>
            </div>
            <div className="space-y-3">
              {getTips().map((tip, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <div className="p-1 rounded-full bg-[#8BB89F]/20 mt-0.5">
                    <span className="text-xs text-[#8BB89F]">{index + 1}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{tip}</p>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>

        {/* Retry Section */}
        {canRetry && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-6"
          >
            <Button
              onClick={handleRetry}
              className="w-full bg-primary hover:bg-primary/90 rounded-full py-6 text-lg"
              data-testid="retry-scan-btn"
            >
              <RotateCcw size={18} className="mr-2" />
              Try Again ({2 - retryCount} attempts left)
            </Button>
          </motion.div>
        )}

        {/* Alternative Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
        >
          <p className="text-sm text-muted-foreground text-center mb-4">
            {canRetry ? 'Or try an alternative method:' : 'Can\'t capture signals right now. Try:'}
          </p>
          
          <div className="space-y-3">
            <Card 
              className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
              onClick={handleSyncWearable}
              data-testid="sync-wearable-btn"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#8BB89F]/20">
                  <Watch size={20} className="text-[#8BB89F]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">Sync Wearable</h4>
                  <p className="text-xs text-muted-foreground">
                    Connect Apple Watch, Fitbit, or other device
                  </p>
                </div>
              </div>
            </Card>

            <Card 
              className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
              onClick={handleManualEntry}
              data-testid="manual-entry-btn"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-[#F8B64C]/20">
                  <Edit3 size={20} className="text-[#F8B64C]" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium">Add Data Manually</h4>
                  <p className="text-xs text-muted-foreground">
                    Enter your vitals from another source
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </motion.div>

        {/* Bottom Actions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-8 flex gap-3"
        >
          <Button
            onClick={handleGoHome}
            variant="outline"
            className="flex-1 rounded-full"
          >
            <Home size={16} className="mr-2" />
            Home
          </Button>
          <Button
            onClick={handleContactSupport}
            variant="outline"
            className="flex-1 rounded-full"
          >
            <MessageSquare size={16} className="mr-2" />
            Support
          </Button>
        </motion.div>

        {/* Attempt counter */}
        {retryCount > 0 && (
          <p className="text-center text-xs text-muted-foreground mt-6">
            Attempt {retryCount} of 2
          </p>
        )}
      </main>
    </div>
  );
}
